import React, { useMemo, useState } from "react";
import styles from "../Styles/SimpleTable.module.css";
import IconButton from "../Images/IconButton";

/**
 * columns: [
 *   { key, header, width?, align?('left'|'center'|'right'),
 *     accessor?(row) -> any,       // näytettävä arvo (oletus: row[key])
 *     render?(row) -> ReactNode,   // kustomoitu solun sisältö
 *     sortAccessor?(row) -> any,   // lajitteluarvo (oletus: accessor)
 *     sortable?: boolean,          // default true
 *     cellClassName?: string | (row)=>string
 *   }
 * ]
 *
 * data: any[]
 * loading?: boolean
 * emptyTitle?: string
 * emptyDescription?: string
 *
 * initialSort?: { key: string, dir: 'asc'|'desc' }
 * pageSize?: number (default 10)
 *
 * onRowClick?: (row) => void
 * actionsHeader?: string
 * actionsRender?: (row) => ReactNode
 *
 * search?: {
 *   value: string,
 *   onChange: (v: string) => void,
 *   placeholder?: string,
 *   keys?: string[] | ((row)=>string) // mille kentille haku kohdistuu
 * }
 *
 * filters?: {
 *   pills?: Array<{                     // “pikasuodatus-pillerit”
 *     field: string,                    // esim. "status"
 *     value: string,                    // valittu arvo
 *     onChange: (v: string)=>void,
 *     items: Array<{value:string,label:string}>
 *   }>,
 *   renderAfterPills?: () => ReactNode,   // 👈 LISÄÄ TÄMÄ
 *   selects?: Array<{                   // esim. tyyppivalitsin
 *     field: string,
 *     value: string,
 *     onChange: (v: string)=>void,
 *     options: Array<{value:string,label:string}>,
 *     placeholder?: string
 *   }>
 * }
 *
 * toolbarLeft?: ReactNode
 * toolbarRight?: ReactNode
 *
 * tableClassName?, headClassName?, rowClassName?(row)?, cellClassName?(row,col)?
 */
export default function SimpleTable({
  columns,
  data = [],
  loading = false,
  emptyTitle = "Ei tietoja",
  emptyDescription = "",
  initialSort,
  pageSize = 10,
  onRowClick,
  actionsHeader = "Toiminnot",
  actionsRender,
  search,
  filters,
  toolbarLeft,
  toolbarRight,
  className,
  tableClassName,
  headClassName,
  rowClassName,
  cellClassName,
}) {
  /* ---------- lajittelu ---------- */
  const [sort, setSort] = useState(
    initialSort || { key: columns?.[0]?.key ?? "_", dir: "asc" }
  );

  const getVal = (col, row) =>
    (col.accessor ? col.accessor(row) : row?.[col.key]);

  const getSortVal = (col, row) =>
    (col.sortAccessor ? col.sortAccessor(row) : getVal(col, row));

  const cmp = (a, b) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (typeof a === "number" && typeof b === "number") return a - b;
    return String(a).localeCompare(String(b), "fi", { sensitivity: "base" });
  };

  /* ---------- suodatus: pillit + selectit ---------- */
  const applyFilters = (rows) => {
    let out = rows;
    if (filters?.pills?.length) {
      filters.pills.forEach(f => {
        if (f.value && f.value !== "all") {
          out = out.filter(r => String(r?.[f.field] ?? "") === String(f.value));
        }
      });
    }
    if (filters?.selects?.length) {
      filters.selects.forEach(s => {
        if (s.value && s.value !== "all") {
          out = out.filter(r => String(r?.[s.field] ?? "") === String(s.value));
        }
      });
    }
    return out;
  };

  /* ---------- haku ---------- */
  const applySearch = (rows) => {
    if (!search?.value?.trim()) return rows;
    const q = search.value.trim().toLowerCase();

    const pick = (row) => {
      if (!search.keys) return JSON.stringify(row);
      if (typeof search.keys === "function") return search.keys(row);
      return search.keys.map(k => String(row?.[k] ?? "")).join(" ");
    };

    return rows.filter(row => pick(row).toLowerCase().includes(q));
  };

  /* ---------- putki ---------- */
  const filtered = useMemo(() => {
    const deps = [
      data,
      ...(filters?.pills || []).map(p => p.value),
      ...(filters?.selects || []).map(s => s.value),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return applyFilters(data);
  }, [data, filters]);

  const searched = useMemo(
    () => applySearch(filtered),
    [filtered, search?.value, search?.keys]
  );

  const sorted = useMemo(() => {
    const col = columns.find(c => c.key === sort.key) || columns[0];
    if (!col) return searched;
    const dirMul = sort.dir === "asc" ? 1 : -1;
    return [...searched].sort((ra, rb) => dirMul * cmp(getSortVal(col, ra), getSortVal(col, rb)));
  }, [searched, columns, sort]);

  /* ---------- sivutus ---------- */
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const start = (page - 1) * pageSize;
  const pageItems = sorted.slice(start, start + pageSize);

  /* ---------- header-komponentti lajittelulle ---------- */
  const SortHeader = ({ col }) => {
    const active = sort.key === col.key;
    const dir = active ? sort.dir : null;
    const sortable = col.sortable !== false;
    const toggle = () => {
      if (!sortable) return;
      const next = active ? (dir === "asc" ? "desc" : "asc") : "asc";
      setSort({ key: col.key, dir: next });
      setPage(1);
    };
    return (
      <button
        className={`${styles.thBtn} ${!sortable ? styles.thBtnDisabled : ""}`}
        onClick={toggle}
        disabled={!sortable}
        aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
        title={typeof col.header === "string" ? `Järjestä: ${col.header}` : "Järjestä"}
      >
        <span>{col.header}</span>
        <span className={styles.sortIcon}>
          {active ? (dir === "asc" ? "▲" : "▼") : ""}
        </span>
      </button>
    );
  };

  /* ---------- toolbar osat ---------- */
  const Pills = () => {
  if (!filters?.pills?.length) return null;
  return (
    <div className={styles.pills}>
      {filters.pills.map((f, idx) => (
        <div key={`p-${idx}`} className={styles.pillGroup}>
          {f.items.map(it => {
            const active = f.value === it.value;
            return (
              <button
                key={it.value}
                className={`${styles.filterPill} ${active ? styles.filterPillActive : ""}`}
                onClick={() => { f.onChange(it.value); setPage(1); }}
              >
                {it.label}
              </button>
            );
          })}
        </div>
      ))}

      {/* 👇 TÄMÄ RIVI ON AINOA UUSI */}
      {filters.renderAfterPills?.()}
    </div>
  );
};

  const Selects = () => {
    if (!filters?.selects?.length) return null;
    return (
      <div className={styles.selects}>
        {filters.selects.map((s, idx) => (
          <select
            key={`s-${idx}`}
            className={styles.select}
            value={s.value}
            onChange={(e) => { s.onChange(e.target.value); setPage(1); }}
            title={s.placeholder || "Suodata"}
          >
            {(s.options || []).map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        ))}
      </div>
    );
  };

  return (
    <div className={`${styles.card} ${className || ""}`}>
      {(filters || search || toolbarLeft || toolbarRight) && (
        <div className={styles.toolbar}>
          <div className={styles.toolbarLeft}>
            {toolbarLeft}
            <Pills />
          </div>
          <div className={styles.toolbarRight}>
            <Selects />
            {toolbarRight}
            {search && (
              <div className={styles.searchWrap}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#f4a100"
                strokeWidth="2"
               >
               <circle cx="11" cy="11" r="8" />
               <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                className={styles.searchInput}
                placeholder={search.placeholder || "Haku…"}
                value={search.value}
                onChange={(e) => { search.onChange(e.target.value); setPage(1); }}
              />
              </div>
            )}
          </div>
        </div>
      )}

      <table className={`${styles.table} ${tableClassName || ""}`}>
        <thead className={`${styles.thead} ${headClassName || ""}`}>
          <tr>
            {columns.map(col => (
              <th
                key={col.key}
                className={`${styles.th} ${col.align ? styles[`a_${col.align}`] : ""}`}
                style={col.width ? { width: col.width } : undefined}
              >
                <SortHeader col={col} />
              </th>
            ))}
            {actionsRender && (
              <th className={`${styles.th} ${styles.a_right}`} style={{ width: 1 }}>
                {actionsHeader}
              </th>
            )}
          </tr>
        </thead>

        <tbody className={styles.tbody}>
          {loading && (
            <tr><td colSpan={columns.length + (actionsRender ? 1 : 0)} className={styles.muted}>Ladataan…</td></tr>
          )}

          {!loading && pageItems.length === 0 && (
            <tr>
              <td colSpan={columns.length + (actionsRender ? 1 : 0)} className={styles.empty}>
                <div className={styles.emptyTitle}>{emptyTitle}</div>
                {emptyDescription && <div className={styles.emptyDesc}>{emptyDescription}</div>}
              </td>
            </tr>
          )}

          {!loading && pageItems.map((row, i) => {
            const key = row.id ?? row.uid ?? i;
            const trExtra = rowClassName ? rowClassName(row) : "";
            const onClick = onRowClick ? () => onRowClick(row) : undefined;
            return (
              <tr
                key={key}
                className={`${onRowClick ? styles.clickRow : ""} ${trExtra}`}
                onClick={onClick}
              >
                {columns.map(col => {
                  const tdExtra =
                    (typeof col.cellClassName === "function"
                      ? col.cellClassName(row, col)
                      : col.cellClassName) ||
                    (typeof cellClassName === "function"
                      ? cellClassName(row, col)
                      : "");
                  return (
                    <td
                      key={col.key}
                      className={`${styles.td} ${col.align ? styles[`a_${col.align}`] : ""} ${tdExtra || ""}`}
                    >
                      {col.render ? col.render(row) : String(getVal(col, row) ?? "")}
                    </td>
                  );
                })}
                {actionsRender && (
                  <td
                    className={`${styles.td} ${styles.a_right}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {actionsRender(row)}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {totalPages > 1 && (
      <div className={styles.pagination}>
       <span>
        <IconButton
          direction="left"
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page <= 1}
        />
       </span>
       <span className={styles.pageCounter}>Sivu {page} / {totalPages}</span>
       <span>
        <IconButton
          direction="right"
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
        />
      </span>
     </div>
    )}
    </div>
  );
}
